<template>
  <div class="relative bg-white pt-16 pb-16">
    <div class="px-4 md:px-10 mx-auto w-full">
      <div class="flex flex-wrap mt-6">
        <div class="w-full px-4">
          <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
            <div class="rounded-t mb-0 px-6 py-4 border-0">
              <div class="flex flex-wrap items-center gap-4">
                <div class="relative w-full md:w-auto max-w-full flex-grow flex-1">
                  <h3 class="font-semibold text-base text-blueGray-700">权限管理</h3>
                  <p class="text-sm text-blueGray-500 mt-2">
                    为指定后台账号分配菜单和高风险操作权限，保存后将直接影响后端接口放行。
                  </p>
                </div>
                <div class="flex flex-wrap gap-3 w-full md:w-auto">
                  <el-input
                    v-model="keyword"
                    placeholder="按邮箱或姓名搜索"
                    clearable
                    style="width: 240px"
                    @keyup.enter="loadAccounts"
                  />
                  <el-button @click="loadAccounts">查询</el-button>
                  <el-button type="primary" @click="createAccount">新增账号</el-button>
                </div>
              </div>
            </div>

            <div class="block w-full px-6 pb-8">
              <el-alert
                v-if="!isSuperAdmin"
                title="只有超管可以管理后台账号权限"
                type="warning"
                :closable="false"
                class="mb-4"
              />

              <div class="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
                <div>
                  <div class="text-sm font-semibold text-blueGray-600 mb-3">已配置账号</div>
                  <el-table
                    v-loading="loadingAccounts"
                    :data="accounts"
                    border
                    highlight-current-row
                    row-key="email"
                    max-height="620"
                    @current-change="handleAccountSelect"
                  >
                    <el-table-column prop="email" label="邮箱" min-width="200" />
                    <el-table-column prop="roleLabel" label="角色" width="100" />
                    <el-table-column label="状态" width="90">
                      <template #default="scope">
                        <el-tag :type="scope.row.status === 'active' ? 'success' : 'info'" size="small">
                          {{ scope.row.status === "active" ? "启用" : "停用" }}
                        </el-tag>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>

                <div>
                  <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <div class="text-sm font-semibold text-blueGray-600">账号配置</div>
                      <div class="text-xs text-blueGray-400 mt-1">
                        角色提供默认权限，下面的开关会作为该账号的最终权限保存。
                      </div>
                    </div>
                    <div class="flex gap-3">
                      <el-button @click="resetDraft">恢复默认</el-button>
                      <el-button type="primary" :disabled="!isSuperAdmin" :loading="saving" @click="saveAccount">
                        保存权限
                      </el-button>
                    </div>
                  </div>

                  <el-form :model="accountDraft" label-width="88px" class="mb-5">
                    <div class="grid gap-4 md:grid-cols-2">
                      <el-form-item label="邮箱">
                        <el-input v-model="accountDraft.email" placeholder="请输入企业微信邮箱" />
                      </el-form-item>
                      <el-form-item label="姓名">
                        <el-input v-model="accountDraft.name" placeholder="可选，默认取最近登录姓名" />
                      </el-form-item>
                      <el-form-item label="角色">
                        <el-select v-model="accountDraft.role" @change="handleRoleChange">
                          <el-option
                            v-for="role in roleOptions"
                            :key="role.value"
                            :label="role.label"
                            :value="role.value"
                          />
                        </el-select>
                      </el-form-item>
                      <el-form-item label="状态">
                        <el-select v-model="accountDraft.status">
                          <el-option label="启用" value="active" />
                          <el-option label="停用" value="disabled" />
                        </el-select>
                      </el-form-item>
                    </div>
                  </el-form>

                  <el-table :data="permissionRows" border>
                    <el-table-column prop="section" label="菜单分组" width="150" />
                    <el-table-column prop="label" label="子菜单" min-width="180" />
                    <el-table-column prop="path" label="页面路径 / 关联操作" min-width="220" />
                    <el-table-column label="可见" width="120" align="center">
                      <template #default="scope">
                        <el-switch
                          v-model="scope.row.visible"
                          :disabled="!isSuperAdmin"
                          @change="handleVisibleChange(scope.row)"
                        />
                      </template>
                    </el-table-column>
                    <el-table-column label="可编辑" width="120" align="center">
                      <template #default="scope">
                        <el-switch
                          v-model="scope.row.editable"
                          :disabled="!isSuperAdmin || !scope.row.visible"
                        />
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { luckdrawApi } from "@/api/luckdraw.js";
import { getUserInfo, updateUserInfo } from "@/utils/auth.js";
import { ROLE_KEYS, ROLE_LABELS } from "@/utils/rbac.js";
import {
  buildPermissionRows,
  buildSavePayload,
  createEmptyAccountDraft,
} from "./rbacPresenter.js";

export default {
  name: "Rbac",
  data() {
    return {
      keyword: "",
      loadingAccounts: false,
      saving: false,
      accounts: [],
      accountDraft: createEmptyAccountDraft(),
      permissionRows: buildPermissionRows(createEmptyAccountDraft()),
    };
  },
  computed: {
    roleOptions() {
      return Object.values(ROLE_KEYS).map((role) => ({
        label: ROLE_LABELS[role],
        value: role,
      }));
    },
    isSuperAdmin() {
      return getUserInfo()?.role === ROLE_KEYS.SUPER_ADMIN;
    },
  },
  mounted() {
    this.loadAccounts();
  },
  methods: {
    buildAccountDraft(account = {}) {
      return {
        ...createEmptyAccountDraft(),
        ...account,
      };
    },
    applyDraft(account = {}) {
      this.accountDraft = this.buildAccountDraft(account);
      this.permissionRows = buildPermissionRows(this.accountDraft);
    },
    createAccount() {
      const currentUser = getUserInfo() || {};
      this.applyDraft({
        email: currentUser.email || "",
      });
    },
    resetDraft() {
      this.permissionRows = buildPermissionRows({
        ...this.accountDraft,
        permissions: {},
      });
    },
    handleRoleChange() {
      this.resetDraft();
    },
    handleVisibleChange(row) {
      if (!row.visible) {
        row.editable = false;
      }
    },
    handleAccountSelect(account) {
      if (!account) {
        return;
      }
      this.applyDraft(account);
    },
    async loadAccounts() {
      this.loadingAccounts = true;
      try {
        const result = await luckdrawApi.getConsoleAccountList({
          keyword: this.keyword,
          page: 1,
          page_size: 100,
        });
        if (result?.sta !== 0) {
          throw new Error(result?.msg || "加载账号失败");
        }

        this.accounts = (result.data?.list || result.data?.items || []).map((item) => ({
          ...item,
          roleLabel: ROLE_LABELS[item.role] || item.role,
        }));

        const currentEmail = getUserInfo()?.email;
        const activeAccount =
          this.accounts.find((item) => item.email === currentEmail) || this.accounts[0];
        if (activeAccount) {
          this.applyDraft(activeAccount);
        } else {
          this.createAccount();
        }
      } catch (error) {
        this.$message.error(error.message || "加载账号失败");
      } finally {
        this.loadingAccounts = false;
      }
    },
    async saveAccount() {
      const payload = buildSavePayload(this.accountDraft, this.permissionRows);
      if (!payload.email) {
        this.$message.warning("请输入后台账号邮箱");
        return;
      }

      this.saving = true;
      try {
        const result = await luckdrawApi.saveConsoleAccount(payload);
        if (result?.sta !== 0) {
          throw new Error(result?.msg || "保存失败");
        }

        const savedAccount = {
          ...result.data,
          roleLabel: ROLE_LABELS[result.data?.role] || result.data?.role,
        };
        const currentUser = getUserInfo() || {};
        if (savedAccount.email === currentUser.email) {
          updateUserInfo({
            role: savedAccount.role,
            status: savedAccount.status,
            permissions: savedAccount.permissions || {},
            username: savedAccount.name || currentUser.username,
            avatar: savedAccount.avatar || currentUser.avatar,
          });
        }

        this.$message.success("权限已保存");
        await this.loadAccounts();
        this.applyDraft(savedAccount);
      } catch (error) {
        this.$message.error(error.message || "保存失败");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
